// a24-search-routing.js
/**
 * Central routing for native Shopify searches.
 *
 * A search submission is converted once into a native Shopify URL. Brand and
 * PS filters are appended before the first result page is requested so the
 * customer never sees chained brand/PS redirects.
 */
(function () {
  'use strict';

  const config = window.A24SearchRoutingConfig || {};
  const searchUrl = String(config.searchUrl || '/search');
  const allProductsUrl = String(config.allProductsUrl || '/collections/all');
  const psParam = String(config.psParam || 'filter.p.m.custom.ps');
  const vendors = Array.isArray(config.vendors) ? config.vendors.filter(Boolean) : [];

  function normalizeWhitespace(value) {
    return String(value || '').trim().replace(/\s+/g, ' ');
  }

  function comparableWords(value) {
    return normalizeWhitespace(value)
      .toLocaleLowerCase('de-DE')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function normalizeQuery(value) {
    const normalize = window.A24SearchNormalization?.normalize;
    return typeof normalize === 'function' ? normalize(value) : normalizeWhitespace(value);
  }

  function isDesignMode() {
    return Boolean(window.A24Theme?.isDesignMode?.() || (window.Shopify && window.Shopify.designMode));
  }

  function shouldProtectEditorClicks() {
    return Boolean(window.A24Theme?.shouldProtectEditorClicks?.());
  }

  function containsWholePhrase(query, phrase) {
    const haystack = comparableWords(query);
    const needle = comparableWords(phrase);
    return Boolean(haystack && needle && (` ${haystack} `).includes(` ${needle} `));
  }

  function detectVendor(query) {
    return vendors
      .filter((vendor) => containsWholePhrase(query, vendor))
      .sort((a, b) => comparableWords(b).length - comparableWords(a).length)[0] || '';
  }

  function parsePs(value) {
    const normalized = String(value || '').trim().replace(',', '.');
    const number = Number.parseFloat(normalized);
    return Number.isFinite(number) ? number : Number.NaN;
  }

  function formatPs(value) {
    return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
  }

  function detectPs(query) {
    const matches = [];
    const expression = /(?:\b(\d+(?:[.,]\d+)?)\s*ps\b|\bps\s*(\d+(?:[.,]\d+)?)\b)/gi;
    let match;
    while ((match = expression.exec(String(query || ''))) !== null) {
      const value = parsePs(match[1] || match[2]);
      if (Number.isFinite(value) && !matches.some((candidate) => candidate === value)) matches.push(value);
    }
    return matches.length === 1 ? matches[0] : Number.NaN;
  }

  function samePath(left, right) {
    const leftUrl = new URL(left, window.location.origin);
    const rightUrl = new URL(right, window.location.origin);
    return leftUrl.pathname.replace(/\/$/, '') === rightUrl.pathname.replace(/\/$/, '');
  }

  function showLoading(message) {
    let overlay = document.querySelector('[data-a24-navigation-loading]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'a24-navigation-loading';
      overlay.dataset.a24NavigationLoading = '';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.innerHTML = '<span class="a24-navigation-loading__spinner" aria-hidden="true"></span><span class="a24-navigation-loading__text"></span>';
      document.body.appendChild(overlay);
    }
    overlay.querySelector('.a24-navigation-loading__text').textContent = message || 'Suche wird geladen…';
    document.body.classList.add('a24-navigation-is-loading');
  }

  function copyFormParams(form, target) {
    const ignored = new Set(['q', 'page', 'a24_ps_search_mode', 'a24_brand_search_mode']);
    new FormData(form).forEach((value, name) => {
      if (ignored.has(name) || value === '' || value === null) return;
      target.searchParams.append(name, value);
    });
  }

  function appendDetectedFilters(target, query) {
    const vendor = detectVendor(query);
    const ps = detectPs(query);

    if (vendor && !target.searchParams.has('filter.p.vendor')) {
      target.searchParams.append('filter.p.vendor', vendor);
    }
    if (Number.isFinite(ps) && !target.searchParams.has(psParam)) {
      target.searchParams.append(psParam, formatPs(ps));
    }
  }

  function buildSearchTarget(form) {
    const target = new URL(form.getAttribute('action') || searchUrl, window.location.origin);
    const queryInput = form.querySelector('input[name="q"]');
    const query = normalizeQuery(queryInput?.value || '');
    if (queryInput) queryInput.value = query;

    copyFormParams(form, target);
    if (query) target.searchParams.set('q', query);
    else target.searchParams.delete('q');

    if (!target.searchParams.has('type')) target.searchParams.set('type', 'product');
    appendDetectedFilters(target, query);
    return target;
  }

  function shouldRouteForm(form) {
    if (shouldProtectEditorClicks()) return false;
    if (!form?.matches?.('form[data-a24-ps-search]')) return false;
    const action = form.getAttribute('action') || searchUrl;
    return samePath(action, searchUrl);
  }

  function routeSubmittedSearch(event) {
    const form = event.target;
    if (!shouldRouteForm(form)) return;

    event.preventDefault();
    const target = buildSearchTarget(form);
    showLoading('Suche wird geladen…');
    window.location.assign(target.toString());
  }

  function routeDirectSearchUrl() {
    if (shouldProtectEditorClicks()) return;
    const current = new URL(window.location.href);
    if (!samePath(current.pathname, searchUrl)) return;

    const query = normalizeQuery(current.searchParams.get('q') || '');
    if (!query) return;

    const target = new URL(current.toString());
    appendDetectedFilters(target, query);
    if (target.toString() === current.toString()) return;

    showLoading('Filter werden vorbereitet…');
    window.location.replace(target.toString());
  }

  function routePredictiveSearchLink(event) {
    if (shouldProtectEditorClicks()) return;
    const link = event.target.closest?.('a.search-dropdown__all');
    if (!link) return;

    const target = new URL(link.href, window.location.origin);
    const query = normalizeQuery(target.searchParams.get('q') || '');
    if (!query) return;

    const routed = new URL(target.toString());
    appendDetectedFilters(routed, query);
    if (routed.toString() === target.toString()) return;

    event.preventDefault();
    showLoading('Suche wird geladen…');
    window.location.assign(routed.toString());
  }

  document.addEventListener('click', routePredictiveSearchLink, true);
  document.addEventListener('submit', routeSubmittedSearch, true);
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', routeDirectSearchUrl, { once: true })
    : routeDirectSearchUrl();

  window.A24SearchRouting = {
    buildSearchTarget,
    detectPs,
    detectVendor,
    showLoading,
    allProductsUrl,
    psParam
  };
}());
